import { Injectable } from '@angular/core';
import { MentionImportResponseModel, MentionImportTableFields } from './mention-csv-import-dialog.service';
import { CsvField } from '@Common/Dialogs/components/CsvImportDialog/csv-import-dialog.service';
import { CsvMentionApiModel } from '@api/mentions/model/csv-mention-api.model';
import { MentionSaveFailResult } from '@api/mentions/model/mention.model';
import { Mention } from '@Common/Mentions/models/mention';
import moment from 'moment-timezone';
import { MentionsApi } from '@api/mentions/mentions.api';
import { LocalizationService } from '@Core/Localization/services/localization.service';


@Injectable()
export class MentionCsvImportEmailReportService {

    constructor(
        public mentionsApi: MentionsApi,
        public localizationService: LocalizationService
    ) {}

    sendEmailReport(
        emails: string[],
        fileNames: string[],
        folders: string[],
        tags: string[],
        pushToMercury: boolean,
        fields: CsvField<CsvMentionApiModel>[],
        data: MentionImportResponseModel[]
    ): void {
        const requestBody = this.prepareEmailReport(emails, fileNames, folders, tags, pushToMercury, fields, data);
        this.mentionsApi.bulkImportEmailReport(requestBody).subscribe();
    }

    prepareEmailReport(
        emails: string[],
        fileNames: string[],
        folders: string[],
        tags: string[],
        pushToMercury: boolean,
        fields: CsvField<CsvMentionApiModel>[],
        data: MentionImportResponseModel[]
    ): any {
        let total, amSuccess, amDuplicate, amFail, mercurySuccess, mercuryDuplicate, mercuryFail;
        total = amSuccess = amDuplicate = amFail = mercurySuccess = mercuryDuplicate = mercuryFail = 0;
        data.forEach(batch => {
            total += batch.total;
            amSuccess += batch.mentionIds.length;
            amDuplicate += batch.duplicateMentionCount || 0;
            amFail += batch.failedToSaveMentions.length;
            if (pushToMercury) {
                mercuryDuplicate += batch.duplicateToPushToMercury || 0;
                mercuryFail += batch.failedToPushToMercury.length;
            }
        });
        mercurySuccess = total - mercuryDuplicate - mercuryFail;
        const mentionsCsv = this.prepareCsvFile(fields, [].concat(...(data.map(x => x.failedToSaveMentions))));
        const mercuryMentionsCsv = pushToMercury ? this.prepareCsvFile(fields, [].concat(...(data.map(x => x.failedToPushToMercury)))) : null;

        const emailReport = {
            recipients: emails,
            fileNames: fileNames,
            foldersAdded: folders,
            tagsAdded: tags,
            addedToSystemCollection: pushToMercury,
            successfulMentionCount: amSuccess,
            successfulMentionMercuryCount: mercurySuccess,
            duplicateMentionsIgnoredCount: amDuplicate,
            errorMentionCount: amFail,
            mentionsCsv: mentionsCsv,
            mercuryDuplicateMentionsIgnoredCount: mercuryDuplicate,
            mercuryErrorMentionCount: mercuryFail,
            mercuryMentionsCsv: mercuryMentionsCsv
        };

        if (!pushToMercury) {
            delete emailReport.successfulMentionMercuryCount;
            delete emailReport.mercuryDuplicateMentionsIgnoredCount;
            delete emailReport.mercuryErrorMentionCount;
            delete emailReport.mercuryMentionsCsv;
        }

        return emailReport;
    }

    prepareCsvFile(fields: CsvField<CsvMentionApiModel>[], data: MentionSaveFailResult[]): string {
        if (data.length === 0) {
            return null;
        }
        let headerRow = '';
        const dataRows = Array.from({ length: data.length }, () => '');
        fields.filter(x => !!x.targetColumn && x.originalField.fieldId !== MentionImportTableFields.publishedDateTime).forEach(field => {
            headerRow += field.originalField.label +  ',';
            data.forEach((saveResult: MentionSaveFailResult, index) => {
                const mention = saveResult.mention;
                let m: Mention, newValue = '';
                switch (field.originalField.fieldId) {
                    case MentionImportTableFields.headline:
                        newValue = mention.title;
                        break;
                    case MentionImportTableFields.body:
                        newValue = mention.body;
                        break;
                    case MentionImportTableFields.publishedDate:
                        newValue = moment(mention.publishedDate).tz('utc').format('YYYY-MM-DD');
                        break;
                    case MentionImportTableFields.publishedTime:
                        newValue = moment(mention.publishedDate).tz('utc').format('hh:mm:ss A');
                        break;
                    case MentionImportTableFields.timezone:
                        newValue = 'UTC';
                        break;
                    case MentionImportTableFields.outlet:
                        newValue = mention.outletName;
                        break;
                    case MentionImportTableFields.author:
                        newValue = mention.contactName;
                        break;
                    case MentionImportTableFields.mediaType:
                        newValue = mention.mediaType;
                        break;
                    case MentionImportTableFields.duration:
                        newValue = '' + mention.clipDuration;
                        break;
                    case MentionImportTableFields.url:
                        newValue = mention.url;
                        break;
                    case MentionImportTableFields.imageUrls:
                        newValue = mention.images.map(x => x.url).join(',');
                        break;
                    case MentionImportTableFields.sentiment:
                        newValue = '' + mention.sentiment;
                        break;
                    case MentionImportTableFields.language:
                        newValue = mention.language;
                        break;
                    case MentionImportTableFields.audienceReach:
                        newValue = '' + mention.audienceReach;
                        break;
                    case MentionImportTableFields.ave:
                        newValue = '' + mention.adValueUsd;
                        break;
                    case MentionImportTableFields.city:
                        newValue = mention.cityName;
                        break;
                    case MentionImportTableFields.state:
                        newValue = mention.stateName;
                        break;
                    case MentionImportTableFields.country:
                        newValue = mention.countryName;
                        break;
                    case MentionImportTableFields.engagementView:
                        m = new Mention(mention);
                        newValue = '' + m.view;
                        break;
                    case MentionImportTableFields.engagementShare:
                        m = new Mention(mention);
                        newValue = '' + m.share;
                        break;
                    case MentionImportTableFields.engagementComment:
                        m = new Mention(mention);
                        newValue = '' + m.reply;
                        break;
                    case MentionImportTableFields.engagementQuote:
                        m = new Mention(mention);
                        newValue = '' + m.quote;
                        break;
                    case MentionImportTableFields.engagementLike:
                        m = new Mention(mention);
                        newValue = '' + m.like;
                        break;

                }
                if (newValue) {
                    const specialCharacters = [',', '\r', '\n', '"', '=', '&', '|'];
                    if (specialCharacters.some(char => newValue.includes(char))) {
                        newValue = newValue.replace(/"/g, '""');
                        newValue = '"' + newValue + '"';
                    }
                    if (newValue === 'undefined' || newValue === 'null') {
                        newValue = '';
                    }
                } else {
                    newValue = '';
                }
                newValue += ',';
                dataRows[index] += newValue;
            });
        });

        headerRow += 'Failed Reason';
        data.forEach((saveResult, index) => {
            dataRows[index] += (saveResult.reason || this.localizationService.transform('Monitoring.mentionImporter.email.failReason.default')) + ',';
        });

        const dataTable = dataRows.reduce((x, y) => x + '\n' + y, '');

        return headerRow + dataTable;
    }
}
